import { CONVERSATION_STATES } from '@hilo/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { isUnavailable } from '../../lib/api';
import { CHANNEL_LABEL, STATE_LABEL } from '../../lib/copy';
import { fetchConnections, keys } from '../../lib/queries';
import { liveState, subscribeLive } from '../../lib/socket';
import { IconSearch } from '../../ui/icons';
import { Select } from '../../ui/Select';
import styles from './InboxFilters.module.css';
import { useInboxFilters } from './useInboxFilters';
import { useInboxList } from './useInboxList';

const ASSIGNMENT_TABS = [
  { value: 'all', label: 'Todas' },
  { value: 'me', label: 'Mías' },
  { value: 'free', label: 'Libres' },
] as const;

/** Estado de la sincronización por sondeo: honesto sobre si lo que se ve está al día. */
function SyncStatus() {
  const { list } = useInboxList();
  const live = useSyncExternalStore(subscribeLive, liveState);
  const offline = list.isError && isUnavailable(list.error);
  const reconnecting = live === 'connecting';
  // En vivo: los cambios llegan al momento. Sin canal, el sondeo cada 10 s sigue al día.
  const state = reconnecting ? 'syncing' : offline ? 'offline' : live === 'live' ? 'live' : list.isFetching ? 'syncing' : 'ok';
  const text = reconnecting
    ? 'Reconectando…'
    : offline
    ? 'Sin conexión · reintentando'
    : live === 'live' ? 'En vivo' : list.isFetching ? 'Actualizando…' : 'Al día · cada 10 s';
  return (
    // Se anuncia la reconexión; el «Actualizando…» de cada 10 s sería ruido para un lector.
    <span className={styles.sync} data-state={state} role={reconnecting || offline ? 'status' : undefined}>
      <span className={styles.dot} aria-hidden="true" />
      {text}
    </span>
  );
}

export function InboxFilters() {
  const { filters, update } = useInboxFilters();
  const connections = useQuery({ queryKey: keys.connections, queryFn: fetchConnections, staleTime: 60_000 });
  const [search, setSearch] = useState(filters.search);

  // La búsqueda se aplica al dejar de escribir, para no pedir una página por cada tecla.
  useEffect(() => {
    if (search === filters.search) return;
    const timer = window.setTimeout(() => update({ buscar: search }), 300);
    return () => window.clearTimeout(timer);
  }, [search, filters.search, update]);

  const trimmed = search.trim();

  return (
    <div className={styles.filters}>
      <div className={styles.head}>
        <h1 className={styles.title}>Bandeja</h1>
        <SyncStatus />
      </div>
      <label className={styles.search}>
        <IconSearch className={styles.searchIcon} />
        <span className="visually-hidden">Buscar por nombre del contacto</span>
        <input type="search" placeholder="Buscar por nombre" value={search} maxLength={100}
          onChange={(event) => setSearch(event.target.value)} />
      </label>
      {trimmed.length === 1 && <p className={styles.hint}>Escribe al menos 2 letras para buscar.</p>}
      <div className={styles.tabs} role="group" aria-label="Asignación">
        {ASSIGNMENT_TABS.map((tab) => (
          <button key={tab.value} type="button" className={styles.tab}
            aria-pressed={filters.assigned === tab.value}
            onClick={() => update({ asignacion: tab.value === 'all' ? null : tab.value })}>
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.selects}>
        <Select variant="pill" label="Página o cuenta" hideLabel active={!!filters.connectionId}
          value={filters.connectionId ?? ''} onChange={(value) => update({ conexion: value })}
          options={[
            { value: '', label: 'Todas las cuentas' },
            ...(connections.data ?? []).map((connection) => ({
              value: connection.id, label: `${connection.displayName} · ${CHANNEL_LABEL[connection.channel]}`,
            })),
          ]} />
        <Select variant="pill" label="Estado" hideLabel active={!!filters.state}
          value={filters.state ?? ''} onChange={(value) => update({ estado: value })}
          options={[
            { value: '', label: 'Todos los estados' },
            ...CONVERSATION_STATES.map((state) => ({ value: state, label: STATE_LABEL[state] })),
          ]} />
      </div>
    </div>
  );
}
